import { useGetClientByIdQuery, useUpdateClientMutation } from '@entities/clients/rtk-query.ts';
import { useGroups } from '@entities/common/groups';
import { isAxiosError } from 'axios';
import cn from 'classnames';
import { useEffect, useMemo, type FC, type ReactNode } from 'react';
import { InfinityList } from '@widgets/shared/InfinityList/index.tsx';
import { useLoadGroups } from '@features/common';
import { GroupCoaches, Group as IconGroup } from '@shared/assets';
import type { Group } from '@shared/types/common';
import { IconCheckbox } from '@shared/ui/inputs/iconCheckbox/index.ts';
import { IconInSquare } from '@shared/ui/smart-icons/IconInSquare/ui.tsx';
import { Blank } from '@src/library/components/common';
import * as st from './styles.module.scss';

type Props = {
  id: number;
};

export const GroupsList: FC<Props> = ({ id }) => {
  const {
    groups,
    selectedGroups,
    isSelectedAtAnyGroups,
    removeFromSelectedGroups,
    addSelectedToGroups,
    setSelectedGroups,
    submitState,
    setSubmitState,
  } = useGroups();
  const { data: client, isLoading, isFetching } = useGetClientByIdQuery(Number(id));
  const [updateClientMutation] = useUpdateClientMutation();

  const { hasMore, loadGroups, status: groupsStatus } = useLoadGroups();
  const status = isLoading || isFetching || groupsStatus === 'loading' ? 'loading' : 'idle';

  useEffect(() => {
    if (client) {
      setSelectedGroups(client.user.custom_groups);
    }
  }, [client, setSelectedGroups]);

  const ArrayNullType: Group[] | [] = groups.filter((group: Group) => {
    if (group.special_type === null) return group;
  });

  const idsSelectedGroups: number[] = selectedGroups.map((group: Group) => group.id);

  const arrayNotSelectedGroups: Group[] | [] = ArrayNullType.filter((group: Group) => {
    if (!idsSelectedGroups.includes(group.id)) return group;
  });

  const arraySelectedGroups: Group[] | [] = selectedGroups.filter((group: Group) => {
    if (group.special_type === null) return group;
  });

  const createFormData = (data: { custom_groups: Group[] }) => {
    const clientData = new FormData();
    const ids: string[] = data.custom_groups.map((group: Group) => {
      return group.id.toString();
    });
    clientData.append('custom_groups', JSON.stringify(ids));
    return clientData;
  };
  const sumbitFunc = async () => {
    const submit = async () => {
      const dataClient = {
        custom_groups: selectedGroups,
      };

      try {
        const formData = createFormData(dataClient);

        await updateClientMutation({ id, body: formData });
      } catch (error) {
        if (isAxiosError(error)) {
          console.log(error);
        }
      }
    };

    await submit();
    setSubmitState(undefined);
  };

  useEffect(() => {
    if (submitState !== undefined) {
      sumbitFunc();
    }
  }, [submitState]);

  const hasAtLeastOneGroupSelected = useMemo(
    () => Boolean(arraySelectedGroups.length),
    [arraySelectedGroups.length]
  );

  return (
    <>
      <InfinityList hasMore={hasMore} loadMore={loadGroups} loading={status === 'loading'}>
        {ArrayNullType.length === 0 && status !== 'loading' && (
          <Blank text="Здесь пока нет групп" />
        )}

        {ArrayNullType.length > 0 && (
          <section className={st.body} key={`${id}-groups`}>
            {arraySelectedGroups.length > 0 && (
              <div
                className={cn(
                  st.groupsPaper,
                  hasAtLeastOneGroupSelected && st.hasAtLeastOneGroupSelected,
                  st.selectedGroups
                )}
              >
                {arraySelectedGroups.map((group) => {
                  if (group.special_type === null) {
                    const isGroupSelected = isSelectedAtAnyGroups(group);
                    return (
                      <GroupNode
                        id={id}
                        group={group}
                        iconCheckboxNode={
                          <IconCheckbox
                            checked={isGroupSelected}
                            onClick={(event) => {
                              event.preventDefault();
                              if (isGroupSelected) {
                                removeFromSelectedGroups(group);
                                setSubmitState(!submitState);
                              }
                            }}
                          />
                        }
                      />
                    );
                  }
                })}
              </div>
            )}

            {arrayNotSelectedGroups.length > 0 && (
              <div
                className={cn(
                  st.groupsPaper,
                  hasAtLeastOneGroupSelected && st.hasAtLeastOneGroupSelected,
                  st.notSelectedGroups
                )}
              >
                {arrayNotSelectedGroups.map((group) => {
                  if (group.special_type === null) {
                    const isGroupSelected = isSelectedAtAnyGroups(group);
                    return (
                      <GroupNode
                        id={id}
                        group={group}
                        iconCheckboxNode={
                          <IconCheckbox
                            checked={isGroupSelected}
                            onClick={(event) => {
                              event.preventDefault();
                              if (!isGroupSelected) {
                                addSelectedToGroups(group);
                                setSubmitState(!submitState);
                              }
                            }}
                          />
                        }
                      />
                    );
                  }
                })}
              </div>
            )}
          </section>
        )}
      </InfinityList>
    </>
  );
};

function GroupNode({
  group,
  id,
  iconCheckboxNode,
}: {
  group: Group;
  iconCheckboxNode: ReactNode;
  id: number;
}) {
  return (
    <div className={st.group} key={`${id}-${group.id}`}>
      <div>{iconCheckboxNode}</div>
      <IconInSquare
        className={st.icon}
        icon={group.special_type === 'coaches' ? <GroupCoaches /> : <IconGroup />}
      />
      <div className={cn(st.title)}>{group.name}</div>
    </div>
  );
}
